import { useState, useEffect } from 'react';
import { Home, Video, Compass, ChevronUp, LogOut } from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarTrigger,
  useSidebar,
} from './ui/sidebar';
import { Link, useLocation } from 'react-router';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import { IUserInfo } from '../model/user';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

interface SubjectCount {
  name: string;
  count: number;
}

interface AppSidebarProps {
  userInfo?: IUserInfo | null;
  subjectCounts?: SubjectCount[];
  onShowLogin?: () => void;
  onLogout?: () => void;
}

export function AppSidebar({
  userInfo,
  subjectCounts = [],
  onShowLogin,
  onLogout,
}: AppSidebarProps) {
  const location = useLocation();
  const { state, setOpen } = useSidebar();
  const isCollapsed = state === 'collapsed';
  const [myVideosOpen, setMyVideosOpen] = useState(true);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const handleItemClick = () => {
    if (isCollapsed) {
      setOpen(true);
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handleConfirmLogout = () => {
    setShowLogoutDialog(false);
    if (onLogout) {
      onLogout();
    }
  };

  // Get user initials for avatar fallback
  const getUserInitials = () => {
    const identifier =
      userInfo?.email ||
      userInfo?.nickname ||
      (userInfo as any)?.nick_name ||
      'U';
    return identifier.charAt(0).toUpperCase();
  };

  const items = [
    {
      title: 'Home',
      url: '/',
      icon: Home,
    },
    {
      title: 'My Videos',
      url: '/my-clips',
      icon: Video,
      hasSubmenu: true,
    },
    {
      title: 'Explore',
      url: '/explore',
      icon: Compass,
    },
  ];

  return (
    <Sidebar
      className="border-r border-black/10 bg-[#fefcfc]"
      collapsible="icon"
    >
      <SidebarHeader className="flex flex-row items-center justify-between px-5 py-7 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:justify-center">
        <Link
          to="/"
          className="flex items-center gap-2 group-data-[collapsible=icon]:hidden"
        >
          <img
            src="https://cdn.jumpstar.app/solve/ai_gen/20251202/7847876a-bcf8-479b-81a9-aef69c5dd7bd.png"
            alt="SolveClip Logo"
            className="h-[30px] object-contain"
          />
        </Link>
        {/* Collapsed state logo */}
        <Link
          to="/"
          className="hidden group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:w-full"
          onClick={handleItemClick}
        >
          <div className="h-[30px] w-[30px] overflow-hidden relative">
            <img
              src="https://cdn.jumpstar.app/solve/ai_gen/20251202/7847876a-bcf8-479b-81a9-aef69c5dd7bd.png"
              alt="SolveClip Logo"
              className="h-full w-auto max-w-none absolute left-0 top-0"
            />
          </div>
        </Link>
        <SidebarTrigger className="h-6 w-6 text-[#302f2b] hover:text-black transition-colors group-data-[collapsible=icon]:hidden" />
      </SidebarHeader>

      <SidebarContent className="px-5 group-data-[collapsible=icon]:px-0">
        <SidebarGroup className="p-0">
          <SidebarGroupContent>
            <SidebarMenu className="gap-2 group-data-[collapsible=icon]:items-center">
              {items.map((item) => (
                <SidebarMenuItem
                  key={item.title}
                  className="group-data-[collapsible=icon]:w-full group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center"
                >
                  {item.hasSubmenu && subjectCounts.length > 0 ? (
                    <Collapsible
                      open={myVideosOpen && !isCollapsed}
                      onOpenChange={setMyVideosOpen}
                      className="w-full"
                    >
                      <div
                        className={`flex items-center w-full group-data-[collapsible=icon]:justify-center rounded-lg transition-colors ${
                          isActive(item.url)
                            ? 'bg-black/5 text-black font-medium'
                            : 'hover:bg-black/5 text-black'
                        }`}
                      >
                        <SidebarMenuButton
                          asChild
                          tooltip={item.title}
                          className="flex-1 h-[38px] px-0 bg-transparent hover:bg-transparent text-inherit group-data-[collapsible=icon]:size-10! group-data-[collapsible=icon]:p-0! group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center"
                        >
                          <Link
                            to={item.url}
                            onClick={() => setMyVideosOpen(true)}
                            className="flex items-center gap-[6px] ml-0 mr-0 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:w-full"
                          >
                            <item.icon className="w-6 h-6 shrink-0 text-inherit ml-[5px] mr-[5px] group-data-[collapsible=icon]:m-0" />
                            <span className="text-[16px] font-normal group-data-[collapsible=icon]:hidden">
                              {item.title}
                            </span>
                          </Link>
                        </SidebarMenuButton>
                        <CollapsibleTrigger
                          asChild
                          className="group-data-[collapsible=icon]:hidden"
                        >
                          <button className="p-1 rounded transition-colors text-black/60">
                            <ChevronUp
                              className={`w-4 h-4 transition-transform duration-200 ${myVideosOpen ? '' : 'rotate-180'}`}
                            />
                          </button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent className="group-data-[collapsible=icon]:hidden">
                        <SidebarMenuSub className="ml-9 border-l-0 pl-0 pr-0 mr-0 mt-1">
                          {subjectCounts.map((subject) => {
                            return (
                              <SidebarMenuSubItem key={subject.name}>
                                <Link
                                  to={`/my-clips?subject=${encodeURIComponent(subject.name)}`}
                                  className="flex items-center justify-between py-[7px] pl-[7px] pr-0 rounded-lg hover:bg-black/5 transition-colors cursor-pointer w-full"
                                >
                                  <div className="flex items-center gap-[6px]">
                                    <span className="text-[16px] text-black">
                                      {subject.name}
                                    </span>
                                  </div>
                                  <div className="w-7 flex justify-center shrink-0">
                                    <span className="text-[16px] text-[#9d9d9d]">
                                      {subject.count}
                                    </span>
                                  </div>
                                </Link>
                              </SidebarMenuSubItem>
                            );
                          })}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </Collapsible>
                  ) : (
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.url)}
                      tooltip={item.title}
                      className={`h-[38px] px-0 rounded-lg transition-all group-data-[collapsible=icon]:size-10! group-data-[collapsible=icon]:p-0! group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center ${
                        isActive(item.url)
                          ? 'bg-black/5 text-black font-medium'
                          : 'bg-transparent text-black hover:text-black'
                      }`}
                    >
                      <Link
                        to={item.url}
                        className="flex items-center gap-[6px] ml-0 mr-0 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:w-full"
                      >
                        <item.icon className="w-6 h-6 shrink-0 text-black ml-[5px] mr-[5px] group-data-[collapsible=icon]:m-0" />
                        <span className="text-[16px] font-normal group-data-[collapsible=icon]:hidden">
                          {item.title}
                        </span>
                      </Link>
                    </SidebarMenuButton>
                  )}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* User login status at bottom left */}
      <SidebarFooter className="px-4 pb-4 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center">
        {userInfo && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="flex items-center gap-2 group-data-[collapsible=icon]:justify-center cursor-pointer hover:bg-gray-100 p-2 rounded-lg transition-colors">
                <Avatar className="w-[30px] h-[30px] border-0">
                  <AvatarFallback className="bg-black text-[#ffe75c] text-[16px] font-semibold flex items-center justify-center leading-none">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm text-black truncate max-w-[120px] group-data-[collapsible=icon]:hidden">
                  {userInfo.email ||
                    userInfo.nickname ||
                    (userInfo as any).nick_name}
                </span>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 mb-2 rounded-xl border border-black/10 shadow-lg bg-white"
            >
              <div className="flex flex-col px-3 py-2.5 border-b border-black/5">
                <span className="text-sm font-medium text-black truncate">
                  {userInfo.email ||
                    userInfo.nickname ||
                    (userInfo as any).nick_name ||
                    'Account'}
                </span>
              </div>
              <DropdownMenuItem
                onClick={handleLogoutClick}
                className="text-black focus:text-black focus:bg-gray-100 m-1 rounded-lg cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </SidebarFooter>

      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent
          className="border! border-[rgba(0,0,0,0.08)]! p-[25px]! max-w-[536px]! gap-4"
          style={{
            borderRadius: '20px',
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
            borderBottomLeftRadius: '20px',
            borderBottomRightRadius: '20px',
            overflow: 'hidden',
          }}
        >
          <AlertDialogHeader className="space-y-2!">
            <AlertDialogTitle
              className="font-semibold! tracking-[-0.4395px]! text-black"
              style={{
                fontFamily: 'Inter, sans-serif',
                lineHeight: '28px',
                fontSize: '18px',
                fontWeight: 600,
              }}
            >
              Are you sure you want to log out?
            </AlertDialogTitle>
            <AlertDialogDescription
              className="tracking-[-0.1504px]!"
              style={{
                color: '#666666',
                fontFamily: 'Inter, sans-serif',
                lineHeight: '20px',
                fontSize: '14px',
                fontWeight: 400,
              }}
            >
              You will not be able to see the generated learning clips.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2! flex-row! justify-end!">
            <AlertDialogCancel
              className="rounded-full! h-9! px-[17px]! border! border-[rgba(0,0,0,0.08)]! text-[14px]! font-medium! tracking-[-0.1504px]! opacity-100!"
              style={{
                fontFamily: 'Inter, sans-serif',
                backgroundColor: '#FFFFFF',
                color: '#000000',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#000000';
                e.currentTarget.style.color = '#FFFFFF';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#FFFFFF';
                e.currentTarget.style.color = '#000000';
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmLogout}
              className="rounded-full! h-9! px-4! text-[14px]! font-medium! tracking-[-0.1504px]! opacity-100!"
              style={{
                fontFamily: 'Inter, sans-serif',
                backgroundColor: '#FFFFFF',
                color: '#000000',
                border: '1px solid rgba(0, 0, 0, 0.08)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#000000';
                e.currentTarget.style.color = '#FFFFFF';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#FFFFFF';
                e.currentTarget.style.color = '#000000';
              }}
            >
              Log out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sidebar>
  );
}
